import { SxProps, Theme } from '@mui/material';
import { isEmpty } from 'lodash';
import { ReactNode, useMemo } from 'react';

import { Badge, BadgeVariants } from '@/components/notifications/Badge/Badge';
import { EstateWaterfallKind } from '@/types/schema';

import {
  useBundleBeneficiaryReportWaterfall,
  useBundleWaterfall,
} from '../../../ClientPresentationDesigner.context';
import { BaseBundleSlideProps } from '../BundleSlide.types';

export type EstateWaterfallBadgesProps = Pick<BaseBundleSlideProps, 'bundle'>;

// use this to force the content of the badges to the center of their containers, otherwise
// they'll align with the bottom of the copy
const BADGE_WRAPPER_SX: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
};

export function EstateWaterfallBadges({ bundle }: EstateWaterfallBadgesProps) {
  const ewBundleWaterfall = useBundleWaterfall(bundle.id);
  const beneficiaryReportBundleWaterfall = useBundleBeneficiaryReportWaterfall(
    bundle.id
  );

  const waterfall = useMemo(() => {
    return beneficiaryReportBundleWaterfall ?? ewBundleWaterfall;
  }, [beneficiaryReportBundleWaterfall, ewBundleWaterfall]);

  const badges = useMemo<ReactNode[]>(() => {
    if (!waterfall) return [];

    const output: ReactNode[] = [];

    if (EstateWaterfallKind.Hypothetical === waterfall.kind) {
      output.push(
        <Badge
          variant={BadgeVariants.Warning}
          display="Hypothetical"
          wrapperSx={BADGE_WRAPPER_SX}
        />
      );
    }

    if (waterfall.growthProfile) {
      output.push(
        <Badge
          variant={BadgeVariants.Primary}
          display={waterfall.growthProfile.displayName}
          wrapperSx={BADGE_WRAPPER_SX}
        />
      );
    }

    return output;
  }, [waterfall]);

  if (!waterfall || isEmpty(badges)) return null;

  return <>{badges}</>;
}
